import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { YesNo } from "@/components/site/Options";
import { brl } from "@/lib/pricing";

const maskCpf = (v) =>
  v.replace(/\D/g, "").slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const Field = ({ label, error, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#163323]">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-xs font-bold text-[#b42318]">{error}</span>}
  </label>
);

const inputCls = "h-12 rounded-sm border-[#d6d0c4] bg-white text-base focus-visible:ring-[#163323]";

export default function GuestForm({ form, set, errors, total }) {
  const bind = (k, mask) => ({
    value: form[k],
    onChange: (e) => set(k, mask ? mask(e.target.value) : e.target.value),
    "data-testid": `input-${k}`,
    className: inputCls,
  });

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Nome completo *" error={errors.nome_completo} className="sm:col-span-2">
        <Input {...bind("nome_completo")} placeholder="Seu nome completo" />
      </Field>
      <Field label="Data de nascimento *" error={errors.data_nascimento}>
        <Input type="date" {...bind("data_nascimento")} max={new Date().toISOString().slice(0, 10)} />
      </Field>
      <Field label="CPF *" error={errors.cpf}>
        <Input {...bind("cpf", maskCpf)} inputMode="numeric" placeholder="000.000.000-00" />
      </Field>
      <Field label="E-mail *" error={errors.email}>
        <Input type="email" {...bind("email")} placeholder="voce@email.com" />
      </Field>
      <Field label="Telefone (WhatsApp) *" error={errors.whatsapp}>
        <Input {...bind("whatsapp", maskPhone)} inputMode="tel" placeholder="(11) 90000-0000" />
      </Field>
      <Field label="Nome do acompanhante *" error={errors.nome_acompanhante}>
        <Input {...bind("nome_acompanhante")} placeholder="Nome completo do acompanhante" />
      </Field>
      <Field label="Número de hóspedes *" error={errors.numero_hospedes}>
        <Input type="number" min={1} {...bind("numero_hospedes")} />
      </Field>
      <Field label="Vai levar pet? (+ R$ 200,00)" className="sm:col-span-2">
        <YesNo value={form.pet} onChange={(v) => set("pet", v)} testid="pet-toggle" />
      </Field>

      <div className="sm:col-span-2">
        <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#163323]">Forma de pagamento *</span>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["pix", "Pix", "Pagamento à vista"],
            ["cartao", "Cartão de crédito", "De 1x a 6x sem juros"],
          ].map(([v, label, sub]) => (
            <button
              key={v}
              type="button"
              data-testid={`payment-${v}`}
              onClick={() => set("forma_pagamento", v)}
              className={`rounded-sm border p-4 text-left transition-[border-color,background-color] duration-300 ${
                form.forma_pagamento === v ? "border-[#163323] bg-[#163323] text-[#faf7f2]" : "border-[#d6d0c4] bg-white text-[#163323] hover:border-[#163323]"
              }`}
            >
              <p className="font-bold uppercase tracking-tight">{label}</p>
              <p className={`text-sm ${form.forma_pagamento === v ? "text-[#e4dfd5]" : "text-[#5a6e61]"}`}>{sub}</p>
            </button>
          ))}
        </div>
        {form.forma_pagamento === "cartao" && (
          <div className="mt-4">
            <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#163323]">Parcelamento (sem juros)</span>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`installment-${n}`}
                  onClick={() => set("parcelas", n)}
                  className={`rounded-sm border px-2 py-3 text-center transition-colors duration-300 ${
                    form.parcelas === n ? "border-[#c86d4b] bg-[#c86d4b] text-white" : "border-[#d6d0c4] bg-white text-[#163323] hover:border-[#c86d4b]"
                  }`}
                >
                  <span className="block text-base font-bold">{n}x</span>
                  <span className="block text-[11px]">{total > 0 ? brl(total / n) : "sem juros"}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {errors.forma_pagamento && <span className="mt-1.5 block text-xs font-bold text-[#b42318]">{errors.forma_pagamento}</span>}
      </div>

      <Field label="Alguma solicitação especial?" className="sm:col-span-2">
        <Textarea
          value={form.solicitacao_especial}
          onChange={(e) => set("solicitacao_especial", e.target.value)}
          data-testid="input-solicitacao_especial"
          rows={4}
          placeholder="Conte para nós como podemos tornar sua estadia ainda mais especial."
          className="rounded-sm border-[#d6d0c4] bg-white text-base focus-visible:ring-[#163323]"
        />
      </Field>
    </div>
  );
}
